import { useState, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import {
  LayoutDashboard,
  FolderPlus,
  Files,
  Settings,
  Users,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  UserCircle,
  Globe,
  Stethoscope,
} from 'lucide-react';
import { signOut } from 'aws-amplify/auth';
import { setLang, LANG_LABELS, type Lang } from './language';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  /** Not yet built — shown disabled. */
  soon?: boolean;
}

const BASE_NAV: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, soon: true },
  { to: '/cases', label: 'Cases', icon: Files },
  { to: '/cases/new', label: 'New case', icon: FolderPlus },
];

/** Users management is GLOBAL_ADMIN-only, so it is appended to the nav conditionally. */
const ADMIN_NAV: NavItem[] = [
  { to: '/admin/users', label: 'Users', icon: Users },
  { to: '/admin/specialties', label: 'Specialties', icon: Stethoscope },
];

const SETTINGS_NAV: NavItem = { to: '/settings', label: 'Settings', icon: Settings, soon: true };

interface LayoutProps {
  userName: string;
  lang: Lang;
  onLangChange: (l: Lang) => void;
  isGlobalAdmin?: boolean;
  children: ReactNode;
}

/**
 * App shell: fixed left sidebar (brand + nav) and a top bar with a right-aligned profile
 * dropdown (language + sign out). Collapsible sidebar on mobile. Mirrors the PAT console layout,
 * adapted to react-bootstrap + our router.
 */
const COLLAPSE_KEY = 'telemedicine.sidebarCollapsed';

export function Layout({ userName, lang, onLangChange, isGlobalAdmin, children }: LayoutProps) {
  const nav: NavItem[] = [
    ...BASE_NAV,
    ...(isGlobalAdmin ? ADMIN_NAV : []),
    SETTINGS_NAV,
  ];
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSE_KEY) === 'true',
  );
  const navigate = useNavigate();

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem(COLLAPSE_KEY, next ? 'true' : 'false');
      return next;
    });
  };

  return (
    <div className="app-shell" data-collapsed={collapsed ? 'true' : 'false'}>
      {/* Sidebar */}
      <aside
        className="app-sidebar d-flex flex-column text-white"
        style={{ background: '#1e293b' }}
        data-open={mobileOpen ? 'true' : 'false'}
      >
        <div className="d-flex align-items-center px-3 border-bottom border-secondary" style={{ height: 56 }}>
          <Stethoscope className="text-info flex-shrink-0" size={20} />
          <span className="fw-semibold ms-2 app-nav-label">Telemedicine</span>
        </div>
        <nav className="flex-grow-1 p-2">
          {nav.map((item) => {
            const Icon = item.icon;
            if (item.soon) {
              return (
                <div
                  key={item.to}
                  className="app-nav-item d-flex align-items-center gap-2 px-3 py-2 rounded text-secondary"
                  style={{ fontSize: 14, opacity: 0.6, cursor: 'not-allowed' }}
                  title={`${item.label} (coming soon)`}
                >
                  <Icon size={18} className="flex-shrink-0" />
                  <span className="app-nav-label">{item.label}</span>
                  <span className="ms-auto badge bg-secondary app-nav-label" style={{ fontSize: 9 }}>soon</span>
                </div>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/cases'}
                title={item.label}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `app-nav-item d-flex align-items-center gap-2 px-3 py-2 rounded text-decoration-none ${
                    isActive ? 'bg-primary text-white' : 'text-light'
                  }`
                }
                style={{ fontSize: 14 }}
              >
                <Icon size={18} className="flex-shrink-0" />
                <span className="app-nav-label">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="d-md-none"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1030 }}
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Main area (offset for the fixed sidebar on md+) */}
      <div className="app-main d-flex flex-column">
        <header
          className="d-flex align-items-center bg-white border-bottom px-3 px-md-4"
          style={{ height: 56, position: 'sticky', top: 0, zIndex: 1020 }}
        >
          {/* Mobile: open the slide-in sidebar. */}
          <button
            className="btn btn-link d-md-none p-0 me-3 text-secondary"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>
          {/* Desktop: collapse/expand the sidebar to an icon rail. */}
          <button
            className="btn btn-link d-none d-md-inline p-0 me-3 text-secondary"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
          </button>
          <div className="flex-grow-1" />
          <Dropdown align="end">
            <Dropdown.Toggle variant="light" size="sm" id="profile-dropdown" className="d-flex align-items-center gap-2">
              <UserCircle size={18} />
              <span className="d-none d-sm-inline">{userName}</span>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Header className="d-flex align-items-center gap-2">
                <Globe size={14} /> Language
              </Dropdown.Header>
              {(['ar', 'en'] as Lang[]).map((l) => (
                <Dropdown.Item
                  key={l}
                  active={lang === l}
                  onClick={() => { setLang(l); onLangChange(l); }}
                >
                  {LANG_LABELS[l]}
                </Dropdown.Item>
              ))}
              <Dropdown.Divider />
              <Dropdown.Item onClick={() => navigate('/settings')} disabled>
                Profile & settings
              </Dropdown.Item>
              <Dropdown.Item onClick={() => signOut().then(() => window.location.reload())}>
                Sign out
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </header>

        <main className="flex-grow-1 p-3 p-md-4 bg-light" style={{ minHeight: 0 }}>
          {children}
        </main>
      </div>
    </div>
  );
}
